// "Por producto": lo pagado por las piezas de cada SKU, sin bonos por umbral ni renta.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import { useState } from 'react';
import { ProductCategoryEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductCategoryEnum';
import { ProductTierEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductTierEnum';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { CellLines, DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { MasterDetailEmpty, MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenErrorBoundary } from '@/shared/patterns/ScreenErrorBoundary';
import { ToolbarMetric } from '@/shared/patterns/TableToolbar';
import { usePayoutRows } from '../../api/usePayoutRows';
import {
    activityCounts,
    applyActivity,
    buildProductRows,
    contributorsNote,
    filterProductRows,
    nextPaidSort,
    PRODUCT_SORT,
    sellersAtLeastTitle,
    sellersText,
    sortRows,
    type ActivityPill,
    type Category,
    type PaidSort,
    type ProductPaidRow,
    type ProductSortKey,
    type Tier,
} from '../../lib/paidRows';
import { DEFAULT_PAYOUT_PERIOD, periodText } from '../../lib/payoutPeriods';
import { CATEGORY_LABEL, TIER_LABEL } from '../../lib/ruleText';
import { FIELD, LABEL } from '../fieldStyles';
import {
    PaidDetailUnavailable,
    PaidFilters,
    PaidListFrame,
    type PaidTabProps,
} from './PaidListFrame';
import { PayoutPeriodSelect } from './PayoutPeriodSelect';
import { ProductPaidDetail } from './ProductPaidDetail';
import { ShareCell } from './ShareCell';

const placed = (row: ProductPaidRow) => row.unitsCount > 0;

export function ProductsPaidTab({ period, onPeriodChange, today, directory }: PaidTabProps) {
    const source = usePayoutRows('SKU', period, today);
    const [search, setSearch] = useState('');
    const [tier, setTier] = useState<Tier | ''>('');
    const [category, setCategory] = useState<Category | ''>('');
    const [pill, setPill] = useState<ActivityPill>('all');
    const [sort, setSort] = useState<PaidSort<ProductSortKey>>({
        key: 'paid',
        direction: 'desc',
    });
    const [selected, setSelected] = useState<string | null>(null);

    const all = buildProductRows(source.rows, directory.products, source.sellersAtLeast);
    const filtered = filterProductRows(all, { search, tier, category });
    const counts = activityCounts(filtered, placed);
    const rows = sortRows(applyActivity(filtered, pill, placed), sort, PRODUCT_SORT);
    const maxCents = Math.max(0, ...rows.map((row) => row.paidCents));
    const when = periodText(period);
    // Sin producto elegido se abre el primero cuando las fuentes ya respondieron, y queda fijado.
    const firstId = rows.at(0)?.sku ?? null;
    if (selected === null && source.isReady && firstId !== null) setSelected(firstId);
    const selectedRow =
        selected === null ? undefined : (all.find((row) => row.sku === selected) ?? rows.at(0));

    const columns: DataTableColumn<ProductPaidRow, ProductSortKey>[] = [
        {
            key: 'sku',
            label: 'SKU',
            width: '110px',
            sortKey: 'sku',
            cell: (row) => <span className="font-mono text-[10.5px]">{row.sku}</span>,
        },
        {
            key: 'name',
            label: 'Producto',
            sortKey: 'name',
            cell: (row) => (
                <CellLines
                    primary={row.name}
                    secondary={row.priceCents === null ? '—' : formatMoneyCents(row.priceCents)}
                />
            ),
        },
        {
            key: 'kind',
            label: 'Gama · Tipo',
            width: '110px',
            cell: (row) => (
                <CellLines
                    primary={row.tier ? TIER_LABEL[row.tier] : 'Sin gama'}
                    secondary={row.category ? CATEGORY_LABEL[row.category] : 'Sin tipo'}
                />
            ),
        },
        {
            key: 'sellers',
            label: 'Vendedores',
            width: '84px',
            numeric: true,
            sortKey: 'sellers',
            cell: (row) => sellersText(row),
            cellTitle: sellersAtLeastTitle,
        },
        {
            key: 'units',
            label: 'Piezas',
            width: '70px',
            numeric: true,
            sortKey: 'units',
            note: when,
            cell: (row) => row.unitsCount,
        },
        {
            key: 'paid',
            label: 'Comisiones · % del total',
            width: '230px',
            sortKey: 'paid',
            note: 'sin umbrales ni renta',
            cell: (row) => (
                <ShareCell
                    paidCents={row.paidCents}
                    totalCents={source.totalPaidCents}
                    maxCents={maxCents}
                />
            ),
        },
    ];

    const filters = (
        <PaidFilters
            onClear={() => {
                setSearch('');
                setTier('');
                setCategory('');
                setPill('all');
                onPeriodChange(DEFAULT_PAYOUT_PERIOD);
            }}
            note="Por producto cuenta la comisión por venta: sin bonos por umbral ni renta de cartera, que son del vendedor. El total no cuadra con las otras pestañas."
        >
            <div>
                <label htmlFor="products-paid-search" className={LABEL}>
                    Búsqueda
                </label>
                <input
                    id="products-paid-search"
                    className={FIELD}
                    placeholder="SKU, marca o modelo"
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                    }}
                />
            </div>
            <PayoutPeriodSelect period={period} onChange={onPeriodChange} today={today} />
            <div>
                <label htmlFor="products-paid-tier" className={LABEL}>
                    Gama
                </label>
                <select
                    id="products-paid-tier"
                    className={FIELD}
                    value={tier}
                    onChange={(event) => {
                        setTier(event.target.value as Tier | '');
                    }}
                >
                    <option value="">Todas</option>
                    {Object.values(ProductTierEnum).map((value) => (
                        <option key={value} value={value}>
                            {TIER_LABEL[value]}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label htmlFor="products-paid-category" className={LABEL}>
                    Tipo de producto
                </label>
                <select
                    id="products-paid-category"
                    className={FIELD}
                    value={category}
                    onChange={(event) => {
                        setCategory(event.target.value as Category | '');
                    }}
                >
                    <option value="">Todos</option>
                    {Object.values(ProductCategoryEnum).map((value) => (
                        <option key={value} value={value}>
                            {CATEGORY_LABEL[value]}
                        </option>
                    ))}
                </select>
            </div>
        </PaidFilters>
    );

    const list = (
        <PaidListFrame
            pills={[
                { key: 'all', label: 'Todos', value: counts.all },
                { key: 'with', label: 'Con ventas', value: counts.with, tone: 'success' },
                { key: 'without', label: 'Sin ventas', value: counts.without },
            ]}
            pill={pill}
            onPill={setPill}
            pillsLabel="Productos con y sin ventas"
            metrics={
                <>
                    <ToolbarMetric label="Productos" value={source.error ? '—' : rows.length} />
                    <ToolbarMetric
                        label={`Comisiones · ${when}`}
                        value={source.error ? '—' : formatMoneyCents(source.totalPaidCents)}
                    />
                </>
            }
            error={source.error}
            errorTitle="No se pudo cargar lo liquidado por producto"
            onRetry={source.refetch}
        >
            <DataTable
                columns={columns}
                rows={rows}
                rowKey={(row) => row.sku}
                caption="Comisiones por producto"
                sort={sort}
                onSortToggle={(key) => {
                    setSort(nextPaidSort(sort, key, ['sku', 'name']));
                }}
                isRowSelected={(row) => row.sku === selectedRow?.sku}
                onRowClick={(row) => {
                    setSelected(row.sku);
                }}
                isLoading={source.isLoading}
                empty="Ningún producto coincide con los filtros."
                density="compact"
            />
        </PaidListFrame>
    );

    const detail = source.error ? (
        <PaidDetailUnavailable />
    ) : selectedRow ? (
        <ScreenErrorBoundary
            title="No se pudo mostrar el detalle del producto"
            resetKeys={[selectedRow.sku]}
        >
            <ProductPaidDetail
                key={selectedRow.sku}
                row={selectedRow}
                totalCents={source.totalPaidCents}
                when={when}
                directory={directory}
                contributorsNote={contributorsNote(source)}
            />
        </ScreenErrorBoundary>
    ) : (
        <MasterDetailEmpty
            title="Selecciona un producto"
            sub="Elige una fila para ver su costo promedio y quién lo colocó."
        />
    );

    return (
        <MasterDetailExplorer
            filters={filters}
            list={list}
            detail={detail}
            storageKey="comisiones-pagadas-producto"
        />
    );
}
